import React, { useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { geocodePlace } from '../VoyagerSearchPageBlock/Services/GeocodingService';
import { logDebug } from 'nsdi-volto-frontend/utils/logger';
import { useLeafletDrawMap } from 'nsdi-volto-frontend/hooks/useLeafletDrawMap';
import type { BBoxPoint } from 'nsdi-volto-frontend/hooks/useLeafletDrawMap';
import { environment } from 'nsdi-volto-frontend/config/environments';
import SearchHistoryDropdown from './components/SearchHistoryDropdown';
import {
  ClearIcon,
  DrawPolygonIcon,
  DrawRectangleIcon,
  ExtentIcon,
  LocationIcon,
  MapControlIcon,
  SearchControlIcon,
  SearchIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from './components/MapIcons';
import { useSearchHistory } from './hooks/useSearchHistory';
import './VoyagerSearchBarBlock.scss';

interface VoyagerSearchBarBlockProps {
  data?: {
    placeholder?: string;
    searchPagePath?: string;
    voyagerSearchUrl?: string; // Full Voyager search URL (absolute or relative)
    showMap?: boolean;
    mapHeight?: number;
    defaultZoom?: number;
    defaultCenter?: string;
    voyagerBaseUrl?: string;
    voyagerConfigId?: string;
    voyagerDisplayId?: string; // Display config ID (disp parameter)
    defaultView?: string; // card, list, table, map
    defaultSort?: string; // e.g., "bytes desc"
    defaultBasemap?: string; // e.g., "ESRI Street Map"
  };
}

const VoyagerSearchBarBlock: React.FC<VoyagerSearchBarBlockProps> = ({
  data,
}) => {
  const history = useHistory();

  // Block configuration with defaults (declared early - used by hook below)
  const placeholder = data?.placeholder || 'What are you looking for?';
  const searchPagePath = data?.searchPagePath || '/voyager-search';
  const showMap = data?.showMap !== false;
  const mapHeight = data?.mapHeight || 400;
  const defaultZoom = data?.defaultZoom || 2;
  const defaultCenter = data?.defaultCenter || '20,0';

  // Voyager-specific config
  const voyagerDisplayId =
    data?.voyagerDisplayId || environment.voyager.displayId;
  const defaultView = data?.defaultView || 'card';
  const defaultSort = data?.defaultSort || 'bytes desc';
  const defaultBasemap = data?.defaultBasemap || 'ESRI Street Map';

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [isEnhanced, setIsEnhanced] = useState(true);
  const [spatialOperator, setSpatialOperator] = useState<
    'within' | 'intersects' | 'contains'
  >('within');
  const [bbox, setBbox] = useState<BBoxPoint[]>([]);

  // Map state
  const [isMapActive, setIsMapActive] = useState(false);

  const iconBaseUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/++plone++static`
      : environment.maps.leafletMarkerIconBaseUrl;

  const {
    mapContainerRef,
    mapRef,
    isMapReady,
    isLeafletLoading,
    drawRectangle,
    drawPolygon,
    clearDrawn,
  } = useLeafletDrawMap({
    defaultCenter,
    defaultZoom,
    iconBaseUrl,
    mapOptions: {
      scrollWheelZoom: false,
      doubleClickZoom: false,
      zoomControl: false,
    },
    enabled: showMap,
    onBboxChange: (newBbox) => {
      setBbox(newBbox);
      if (newBbox.length === 4) {
        const sw = newBbox[0];
        const ne = newBbox[2];
        const bboxStr = `${sw.longitude.toFixed(2)} ${sw.latitude.toFixed(2)} ${ne.longitude.toFixed(2)} ${ne.latitude.toFixed(2)}`;
        setLocationQuery(`📍 ${bboxStr}`);
      } else {
        setLocationQuery('');
      }
    },
  });

  // Recent/Saved searches state
  const [showSearchHistory, setShowSearchHistory] = useState(true);
  const [activeSearchTab, setActiveSearchTab] = useState<'recent' | 'saved'>(
    'recent',
  );
  const { recentSearches, savedSearches, saveToRecentSearches } =
    useSearchHistory();

  // Invalidate map size when expanded/collapsed
  useEffect(() => {
    if (mapRef.current && isMapReady) {
      setTimeout(() => {
        mapRef.current?.invalidateSize();
      }, 300);
    }
  }, [isMapReady, mapRef]);

  // Build search URL with Voyager-compatible query parameters
  const buildSearchUrl = useCallback((): string => {
    const params = new URLSearchParams();

    // Search query
    if (searchQuery.trim()) {
      params.set('q', searchQuery.trim());
    }

    // Spatial/place query - format: "minLng minLat maxLng maxLat"
    if (bbox.length === 4) {
      // bbox is [SW, SE, NE, NW] - we need minLng, minLat, maxLng, maxLat
      const minLng = bbox[0].longitude;
      const minLat = bbox[0].latitude;
      const maxLng = bbox[2].longitude;
      const maxLat = bbox[2].latitude;
      const placeStr = `${minLng} ${minLat} ${maxLng} ${maxLat}`;
      params.set('place', placeStr);
      params.set('place.finder', 'esri');
      params.set('place.op', spatialOperator);
      params.set('settings', 'any');
    } else if (locationQuery.trim() && !locationQuery.startsWith('📍')) {
      // Text-based location query
      params.set('place', locationQuery.trim());
      params.set('place.finder', 'esri');
      params.set('place.op', spatialOperator);
      params.set('settings', 'any');
    }

    // Display configuration
    if (voyagerDisplayId) {
      params.set('disp', voyagerDisplayId);
    }

    // Facet sorting (standard Voyager facets)
    params.set('f.grp_Geography.facet.sort', 'index');
    params.set('f.grp_Sub\\-Region.facet.sort', 'index');
    params.set('f.grp_State.facet.sort', 'index');
    params.set('f.grp_County.facet.sort', 'index');
    params.set('f.grp_Bureau_\\%2F_Department.facet.sort', 'index');

    // View and display options
    params.set('view', defaultView);
    params.set('sort', defaultSort);
    params.set('basemap', defaultBasemap);

    const queryString = params.toString();

    // Use searchPagePath - supports both internal paths and external URLs
    const basePath = searchPagePath;

    // Check if it's an absolute URL (external)
    try {
      const url = new URL(basePath);
      // It's an absolute URL, append query params properly
      url.search = queryString;
      return url.toString();
    } catch {
      // It's a relative path
      return queryString ? `${basePath}?${queryString}` : basePath;
    }
  }, [
    searchQuery,
    spatialOperator,
    bbox,
    locationQuery,
    searchPagePath,
    voyagerDisplayId,
    defaultView,
    defaultSort,
    defaultBasemap,
  ]);

  // Handle search
  const handleSearch = useCallback(async () => {
    // If location query is provided (and not bbox coordinates), geocode it
    if (locationQuery.trim() && !locationQuery.startsWith('📍')) {
      try {
        const geocodeResult = await geocodePlace(locationQuery.trim());
        if (geocodeResult && geocodeResult.bboxCorners) {
          const newBbox: BBoxPoint[] = geocodeResult.bboxCorners.map(
            (corner: any) => ({
              longitude: corner.longitude,
              latitude: corner.latitude,
            }),
          );
          setBbox(newBbox);
        }
      } catch (error) {
        logDebug('Geocoding error:', error);
      }
    }

    // Save to recent searches
    const searchText = searchQuery.trim() || locationQuery.trim();
    if (searchText && !searchText.startsWith('📍')) {
      saveToRecentSearches(searchText);
    }

    // Navigate to search page with parameters
    const url = buildSearchUrl();

    // Check if URL is absolute (external) or relative
    if (url.startsWith('http://') || url.startsWith('https://')) {
      // External URL - open in same window or new tab
      window.location.href = url;
    } else {
      // Relative URL - use React Router
      history.push(url);
    }
  }, [
    locationQuery,
    searchQuery,
    buildSearchUrl,
    history,
    saveToRecentSearches,
  ]);

  // Handle search from history
  const handleSearchFromHistory = useCallback(
    (search: string) => {
      setSearchQuery(search);
      setShowSearchHistory(false);
      // Auto-navigate
      setTimeout(() => {
        const params = new URLSearchParams();
        params.set('q', search);

        // Check if searchPagePath is an external URL
        try {
          const url = new URL(searchPagePath);
          url.search = params.toString();
          window.location.href = url.toString();
        } catch {
          // Relative path - use React Router
          history.push(`${searchPagePath}?${params.toString()}`);
        }
      }, 100);
    },
    [history, searchPagePath],
  );

  // Handle enhanced toggle
  const handleEnhancedToggle = useCallback(() => {
    setIsEnhanced((prev) => !prev);
  }, []);

  // Handle spatial operator change
  const handleOperatorChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSpatialOperator(
        e.target.value as 'within' | 'intersects' | 'contains',
      );
    },
    [],
  );

  // Handle search button click (crosshair button) - move search bar to center
  const handleSearchButtonClick = useCallback(() => {
    setIsMapActive(false);
  }, []);

  // Handle map button click - move search bar to bottom
  const handleMapButtonClick = useCallback(() => {
    setIsMapActive(true);
  }, []);

  // Handle reset extent - zoom to default view
  const handleResetExtent = useCallback(() => {
    if (mapRef.current) {
      const [lat, lng] = defaultCenter
        .split(',')
        .map((c) => parseFloat(c.trim()));
      const centerLat = isNaN(lat) ? 20 : lat;
      const centerLng = isNaN(lng) ? 0 : lng;
      mapRef.current.setView([centerLat, centerLng], defaultZoom);
    }
  }, [defaultCenter, defaultZoom, mapRef]);

  // Handle zoom in
  const handleZoomIn = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.zoomIn();
    }
  }, [mapRef]);

  // Handle zoom out
  const handleZoomOut = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.zoomOut();
    }
  }, [mapRef]);

  // Handle draw rectangle
  const handleDrawRectangle = useCallback(() => {
    drawRectangle();
  }, [drawRectangle]);

  // Handle draw polygon
  const handleDrawPolygon = useCallback(() => {
    drawPolygon();
  }, [drawPolygon]);

  // Handle clear drawn items
  const handleClearDrawn = useCallback(() => {
    clearDrawn();
    setBbox([]);
    setLocationQuery('');
  }, [clearDrawn]);

  return (
    <div className="voyager-search-bar-block">
      {/* Map Container */}
      {showMap && (
        <div className="map-container" style={{ height: `${mapHeight}px` }}>
          {isLeafletLoading && (
            <div className="map-loading">
              <div className="loading-spinner"></div>
              <p>Loading map...</p>
            </div>
          )}
          <div
            ref={mapContainerRef}
            className="map-view"
            style={{ height: '100%', width: '100%' }}
          />
        </div>
      )}

      {/* Custom Control Buttons - outside map container for proper z-index */}
      {showMap && (
        <div className="custom-map-controls">
          <button
            className={`control-btn search-control ${!isMapActive ? 'active' : ''}`}
            onClick={handleSearchButtonClick}
            title="Show search bar"
            aria-label="Show search bar"
          >
            <SearchControlIcon />
          </button>
          <button
            className={`control-btn map-control ${isMapActive ? 'active' : ''}`}
            onClick={handleMapButtonClick}
            title="Draw on map"
            aria-label="Draw on map"
          >
            <MapControlIcon />
          </button>
        </div>
      )}

      {/* Map Tools Control Group - only visible in draw mode */}
      {showMap && isMapActive && (
        <div className="map-tools-controls">
          <button
            className="control-btn zoom-in-control"
            onClick={handleZoomIn}
            title="Zoom in"
            aria-label="Zoom in"
          >
            <ZoomInIcon />
          </button>
          <button
            className="control-btn zoom-out-control"
            onClick={handleZoomOut}
            title="Zoom out"
            aria-label="Zoom out"
          >
            <ZoomOutIcon />
          </button>
          <button
            className="control-btn extent-control"
            onClick={handleResetExtent}
            title="Default extent"
            aria-label="Default extent"
          >
            <ExtentIcon />
          </button>
          <button
            className="control-btn draw-rectangle-control"
            onClick={handleDrawRectangle}
            title="Draw rectangle"
            aria-label="Draw rectangle"
          >
            <DrawRectangleIcon />
          </button>
          <button
            className="control-btn draw-polygon-control"
            onClick={handleDrawPolygon}
            title="Draw polygon"
            aria-label="Draw polygon"
          >
            <DrawPolygonIcon />
          </button>
          <button
            className="control-btn clear-control"
            onClick={handleClearDrawn}
            title="Clear selection"
            aria-label="Clear selection"
          >
            <ClearIcon />
          </button>
        </div>
      )}

      {/* Search Bar Overlay */}
      <div
        className={`search-bar-overlay ${isMapActive ? 'bottom' : 'center'}`}
      >
        <div
          className="search-bar-container"
          role="presentation"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <div className="enhanced-search-bar">
            {/* Text search input */}
            <div className="search-field text-search">
              <input
                type="text"
                className="search-input"
                placeholder={placeholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setShowSearchHistory(true)}
                onBlur={() =>
                  setTimeout(() => setShowSearchHistory(false), 200)
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
              />
            </div>

            {/* Enhanced toggle */}
            <div className="search-field enhanced-toggle">
              <label className="toggle-switch" aria-label="Enhanced">
                <input
                  type="checkbox"
                  checked={isEnhanced}
                  onChange={handleEnhancedToggle}
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="toggle-label">Enhanced</span>
            </div>

            {/* Spatial operator dropdown */}
            <div className="search-field spatial-operator">
              <select
                className="operator-select"
                value={spatialOperator}
                onChange={handleOperatorChange}
              >
                <option value="within">Within</option>
                <option value="intersects">Intersects</option>
                <option value="contains">Contains</option>
              </select>
            </div>

            {/* Location search input */}
            <div className="search-field location-search">
              {isMapActive && (
                <span className="location-icon">
                  <LocationIcon />
                </span>
              )}
              <input
                type="text"
                className="search-input"
                placeholder={
                  isMapActive
                    ? 'Draw region on the map'
                    : 'Where is it located?'
                }
                value={locationQuery}
                onChange={(e) => setLocationQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
              />
            </div>

            {/* Search button */}
            <button
              className="search-button"
              onClick={handleSearch}
              aria-label="Search"
            >
              <SearchIcon />
            </button>
          </div>

          {/* Search History Dropdown */}
          {showSearchHistory && !isMapActive && (
            <SearchHistoryDropdown
              activeTab={activeSearchTab}
              recentSearches={recentSearches}
              savedSearches={savedSearches}
              onTabChange={setActiveSearchTab}
              onSearchSelect={handleSearchFromHistory}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default VoyagerSearchBarBlock;
